// Install the service worker and offer installing pulsar as an app.
import "./push";

if ("serviceWorker" in navigator) navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch(() => {});

// no zooming (ADR 0101): Safari ignores user-scalable=no, but its pinch comes as gesture events, which can be cancelled;
// double-tap zoom is off through touch-action on <html>. The chessboard's own two-finger arrows are touch events, untouched
for (const type of ["gesturestart", "gesturechange", "gestureend"]) document.addEventListener(type, (e) => e.preventDefault(), { passive: false });

// the stars drift slower than the page (parallax): their layer moves by a share of the scroll, wrapped at the star
// tile's size so it never runs out; with reduced motion they stay still. Only transform changes: no repaint per frame
const sky = document.querySelector<HTMLElement>(".sky");
if (sky && !matchMedia("(prefers-reduced-motion: reduce)").matches) {
  const SHARE = 0.35, TILE = 240; // the tile is the stars' SVG size (src/web/layout.tsx)
  let queued = false;
  const drift = () => {
    queued = false;
    // with overscroll (iOS) scrollY goes below 0: the modulo stays within one tile either way
    const y = (((window.scrollY * SHARE) % TILE) + TILE) % TILE;
    sky.style.transform = `translate3d(0,${-y}px,0)`;
  };
  addEventListener("scroll", () => { if (!queued) { queued = true; requestAnimationFrame(drift); } }, { passive: true });
  drift();
}

const DISMISS_KEY = "pulsar-install-dismissed";
const QUIET_MS = 3 * 86_400_000;
const banner = document.getElementById("install");
const standalone = matchMedia("(display-mode: standalone)").matches || (navigator as any).standalone === true;
const dismissedRecently = () => { try { return Date.now() - Number(localStorage.getItem(DISMISS_KEY) ?? 0) < QUIET_MS; } catch { return false; } };
// once per browser session, never on pages that wait for something (they may reload)
const shownThisSession = () => { try { return sessionStorage.getItem("pulsar-install-shown") === "1"; } catch { return false; } };
const markShown = () => { try { sessionStorage.setItem("pulsar-install-shown", "1"); } catch {} };

if (banner && !standalone && !dismissedRecently() && !shownThisSession() && !banner.dataset.quiet) {
  const go = banner.querySelector<HTMLButtonElement>(".go")!;
  const close = banner.querySelector<HTMLButtonElement>(".x")!;
  const hide = () => banner.classList.remove("show");
  close.onclick = () => { try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {} hide(); };
  window.addEventListener("appinstalled", hide);

  // Chrome, Edge, Android: the browser's own install prompt, shown from our button
  let deferred: any = null;
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferred = e;
    go.onclick = async () => { deferred.prompt(); const r = await deferred.userChoice; if (r.outcome === "accepted") hide(); deferred = null; };
    setTimeout(() => { banner.classList.add("show"); markShown(); }, 1500);
  });

  // iPhone and iPad have no install event: explain the Share menu instead
  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (ios) {
    go.onclick = () => { banner.querySelector("small")!.textContent = banner.dataset.ios ?? ""; go.remove(); };
    setTimeout(() => { banner.classList.add("show"); markShown(); }, 1500);
  }
}

// Family goal countdown (ADR 0035): keep "… left" current without reloading the page.
function tickCountdowns() {
  for (const el of document.querySelectorAll<HTMLElement>(".countdown[data-ends]")) {
    const ms = Math.max(0, new Date(el.dataset.ends!).getTime() - Date.now());
    const d = Math.floor(ms / 86_400_000), h = Math.floor((ms % 86_400_000) / 3_600_000), m = Math.floor((ms % 3_600_000) / 60_000);
    const fill = (tpl: string | undefined, v: Record<string, number>) => (tpl ?? "").replace(/\{(\w+)\}/g, (_, k) => String(v[k] ?? ""));
    const text = ms >= 3 * 86_400_000 ? fill(el.dataset.lDays, { days: d }) : ms >= 86_400_000 ? fill(el.dataset.lDh, { d, h })
      : h > 0 ? fill(el.dataset.lHm, { h, m }) : fill(el.dataset.lM, { m });
    el.textContent = text;
    // the next goal starts while the page is open: show it (only after counting down here, so a late start can't loop)
    if (el.dataset.reload && ms > 0) el.dataset.counting = "1";
    else if (el.dataset.reload && el.dataset.counting) { delete el.dataset.counting; location.reload(); }
  }
}
setInterval(tickCountdowns, 30_000);

// A page from before a deploy is stale: when it gets the focus again (back to the tab or the app), it reloads, unless
// that would lose something (a lesson in progress marks itself with data-keep, or there is typed text not yet sent).
let lastVersionCheck = 0;
async function reloadIfStale() {
  if (document.visibilityState !== "visible" || Date.now() - lastVersionCheck < 60_000) return;
  lastVersionCheck = Date.now();
  const mine = document.documentElement.dataset.boot;
  if (!mine || document.querySelector("[data-keep]")) return;
  const typed = [...document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("textarea, input[type=text], input[type=email], input:not([type])")]
    .some((f) => f.value.trim() && f.value !== f.defaultValue);
  if (typed) return;
  try {
    const r = await fetch("/version", { cache: "no-store" });
    if (r.ok && (await r.json()).boot !== mine) location.reload();
  } catch { /* offline: keep the page */ }
}
document.addEventListener("visibilitychange", () => { void reloadIfStale(); });
window.addEventListener("focus", () => { void reloadIfStale(); });

// Tab badges stay current while a page is open (a family member moved, a report came in): asked when the page comes back
// into view and every 30 s while it is visible. Same markup as the server's Count component.
function setCount(link: Element | null, n: number) {
  if (!link) return;
  let badge = link.querySelector<HTMLElement>(".count");
  if (!n) { badge?.remove(); return; }
  if (!badge) { badge = document.createElement("span"); badge.className = "count"; link.append(badge); }
  badge.textContent = n > 99 ? "99+" : String(n);
}
async function refreshCounts() {
  if (document.visibilityState !== "visible" || !document.querySelector("nav.tabs")) return;
  try {
    const r = await fetch("/api/counts", { cache: "no-store" });
    if (!r.ok) return;
    const n = (await r.json()) as { home: number; chess: number; me: number };
    setCount(document.querySelector('nav.tabs a[href="/"]'), n.home);
    setCount(document.querySelector('main a[href="/family/chat"]'), n.home); // the Chat button on Home
    setCount(document.querySelector('nav.tabs a[href="/chess"]'), n.chess);
    setCount(document.querySelector("header a.me"), n.me);
    setCount(document.querySelector('main a[href="/admin"]'), n.me); // the Admin button on the Me page
  } catch { /* offline: keep what is shown */ }
}
// InfoDialog (layout.tsx): its info button opens the dialog it names; Close or a tap outside it closes it
document.addEventListener("click", (e) => {
  const target = e.target as Element | null;
  const opener = target?.closest?.("[data-opens]") as HTMLElement | null;
  const dialog = opener && document.getElementById(opener.dataset.opens ?? "");
  if (dialog instanceof HTMLDialogElement) { dialog.showModal(); return; }
  // a tap on the backdrop lands on the dialog itself, outside its box
  if (target instanceof HTMLDialogElement && target.open) {
    const r = target.getBoundingClientRect(), p = e as MouseEvent;
    if (p.clientX < r.left || p.clientX > r.right || p.clientY < r.top || p.clientY > r.bottom) target.close();
  }
});
document.addEventListener("visibilitychange", () => { void refreshCounts(); });
window.addEventListener("focus", () => { void refreshCounts(); });
// a page that just changed what is waiting (a move made, a game over) asks for fresh badges at once
window.addEventListener("pulsar:counts", () => { void refreshCounts(); });
setInterval(() => { void refreshCounts(); }, 30_000);
// back to a page the browser kept in memory: after a chess move it shows the game as still waiting, so load it again
const loadedAt = Date.now();
window.addEventListener("pageshow", (e) => {
  if (!e.persisted) return;
  let moved = 0;
  try { moved = Number(sessionStorage.getItem("pulsar-moved") ?? 0); } catch {}
  if (moved > loadedAt) location.reload(); else void refreshCounts();
});

// the home page's Quick start: fresh on coming back to the page and every 30 s while it is open
const quickStart = async () => {
  const box = document.getElementById("quick-start");
  if (!box || document.visibilityState !== "visible") return;
  try {
    const r = await fetch("/api/home/quick-start", { cache: "no-store" });
    if (r.ok) box.outerHTML = await r.text();
  } catch { /* offline: keep what is shown */ }
};
if (document.getElementById("quick-start")) {
  document.addEventListener("visibilitychange", () => { void quickStart(); });
  window.addEventListener("focus", () => { void quickStart(); });
  window.addEventListener("pageshow", (e) => { if (e.persisted) void quickStart(); }); // back from a lesson or game
  setInterval(() => { void quickStart(); }, 30_000);
}

// the way back on the family chat and its attachments page: when the page before this one is the link's target, step
// back in the history instead of adding another entry, so the browser's back goes on to the page before (home, chat, files)
document.addEventListener("click", (e) => {
  const a = (e.target as Element).closest<HTMLAnchorElement>("a.chat-back");
  if (!a || e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || history.length < 2) return;
  try {
    const from = new URL(document.referrer);
    if (from.origin === location.origin && from.pathname === new URL(a.href).pathname) { e.preventDefault(); history.back(); }
  } catch { /* no referrer: the link goes there as usual */ }
});
